import { useEffect, useState } from "react";
import { api, API_BASE, getSessionTokenForTest } from "@/lib/api";
import { fmtINR, fmtNum, todayISO } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import BarChart from "@/components/BarChart";

export default function SalesSummary() {
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0,10));
  const [to, setTo] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const r = await api.get("/reports/sales_summary", { params: { date_from: from, date_to: to } });
      setData(r.data);
    })();
  }, [from, to]);

  const exportCsv = (name) => {
    const tok = getSessionTokenForTest();
    const url = `${API_BASE}/reports/export/${name}` + (tok ? `?auth=${tok}` : "");
    window.open(url, "_blank");
  };

  // Group by ISO week for the "Weekly" chart
  const series = data?.series || [];
  const dailyChart = series.slice(-14).map((s) => ({
    label: s.date.slice(5),  // MM-DD
    values: [s.hsd_l, s.ms_l, s.cng_kg],
  }));

  const weekly = {};
  for (const s of series) {
    const d = new Date(s.date);
    const y = d.getUTCFullYear();
    const firstJan = new Date(Date.UTC(y, 0, 1));
    const week = Math.ceil(((d - firstJan) / 86400000 + firstJan.getUTCDay() + 1) / 7);
    const key = `${y}-W${String(week).padStart(2, "0")}`;
    const row = weekly[key] = weekly[key] || { label: key.slice(2), values: [0, 0, 0] };
    row.values[0] += s.hsd_l;
    row.values[1] += s.ms_l;
    row.values[2] += s.cng_kg;
  }
  const weeklyChart = Object.values(weekly).slice(-8);

  const fuelSplit = data ? [
    { label: "HSD", values: [data.hsd_litres] },
    { label: "MS", values: [data.ms_litres] },
    { label: "CNG", values: [data.cng_kg] },
  ] : [];

  return (
    <div className="space-y-4" data-testid="sales-root">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Sales Analytics</h1>
          <p className="text-sm text-slate-600 mt-1">Daily / weekly time-series, by fuel type.</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <div><label className="text-xs font-bold uppercase text-slate-600">From</label>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="block h-11 border border-slate-300 rounded-sm px-3 bg-white" /></div>
          <div><label className="text-xs font-bold uppercase text-slate-600">To</label>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="block h-11 border border-slate-300 rounded-sm px-3 bg-white" /></div>
          <Button variant="outline" className="border-slate-300 h-11" onClick={() => exportCsv("shift_entries.csv")}>Shifts CSV</Button>
          <Button variant="outline" className="border-slate-300 h-11" onClick={() => exportCsv("debtor_entries.csv")}>Debtors CSV</Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="HSD Litres" v={fmtNum(data.hsd_litres)} />
            <Stat label="MS Litres" v={fmtNum(data.ms_litres)} />
            <Stat label="CNG Kg" v={fmtNum(data.cng_kg)} />
            <Stat label="Lubricants" v={fmtINR(data.lubricants_total)} />
            <Stat label="Fuels ₹" v={fmtINR(data.sales_fuels_rs)} big />
            <Stat label="CNG ₹" v={fmtINR(data.sales_cng_rs)} big />
            <Stat label="Cash (Fuels)" v={fmtINR(data.cash_in_hand_fuels)} />
            <Stat label="Cash (CNG)" v={fmtINR(data.cash_in_hand_cng)} />
          </div>

          <Card className="p-4 border-2 border-slate-300 rounded-sm" data-testid="chart-daily">
            <h3 className="font-bold text-slate-800 mb-2">Last 14 days — HSD / MS / CNG</h3>
            {dailyChart.length > 0 ? (
              <BarChart data={dailyChart} seriesLabels={["HSD (L)", "MS (L)", "CNG (kg)"]} formatValue={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0)} />
            ) : <div className="text-sm text-slate-500">No entries in range.</div>}
          </Card>

          <Card className="p-4 border-2 border-slate-300 rounded-sm" data-testid="chart-weekly">
            <h3 className="font-bold text-slate-800 mb-2">Last 8 weeks — HSD / MS / CNG</h3>
            {weeklyChart.length > 0 ? (
              <BarChart data={weeklyChart} seriesLabels={["HSD (L)", "MS (L)", "CNG (kg)"]} formatValue={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0)} />
            ) : <div className="text-sm text-slate-500">Insufficient weeks.</div>}
          </Card>

          <Card className="p-4 border-2 border-slate-300 rounded-sm" data-testid="chart-fuel-split">
            <h3 className="font-bold text-slate-800 mb-2">By fuel type (total, selected range)</h3>
            <BarChart data={fuelSplit} seriesLabels={["Volume"]} colors={["#0f172a"]} formatValue={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0)} />
          </Card>

          <Card className="p-3 border-2 border-slate-300 rounded-sm">
            <h3 className="font-bold text-slate-800 mb-2">Payments by mode × category</h3>
            <table className="dense-table w-full text-sm">
              <thead><tr><th>Mode</th><th>Category</th><th>Total ₹</th></tr></thead>
              <tbody>
                {Object.entries(data.by_payment_mode || {}).map(([k, v]) => {
                  const [pm, cat] = k.split("|");
                  return <tr key={k}><td>{pm}</td><td>{cat}</td><td className="num font-bold">{fmtINR(v)}</td></tr>;
                })}
              </tbody>
            </table>
          </Card>
        </>
      )}
    </div>
  );
}

function Stat({ label, v, big }) {
  return (
    <div className="bg-white border-2 border-slate-300 p-3 rounded-sm">
      <div className="text-xs font-bold uppercase tracking-wider text-slate-600">{label}</div>
      <div className={"font-mono font-bold text-slate-900 mt-1 " + (big ? "text-3xl" : "text-xl")}>{v}</div>
    </div>
  );
}
